import * as angular from 'angular';

import * as WidgetModels from '../../../models/widgets';

import {
    AcquisitionStrategy,
    ActualDataType,
    AggregationConfiguration,
    AggregationMode as AggregationModeEnum,
    ContextualField,
    DateOrder,
    GroupDisplayName,
    IFrequency,
    Limit,
    PeriodReference as PeriodReferenceEnum,
    PropertyType,
    RowSpacing,
    SelectionStrategyList,
    TimeInterval
} from '../../../models';
import { ValueTypes } from '../../../common/ValueTypes';
import { aggregationModes } from '../../../common/aggregationModes';

export const frequencyErrors = [
    { key: 'minOrZero', value: 'Invalid interval format. Interval must be an integer greater than or equal to 0.' },
    { key: 'min', value: 'Invalid interval format. Interval must be an integer greater than 0.' },
    { key: 'number', value: 'Invalid interval format. Interval must be an integer.' },
    { key: 'interval', value: 'Unsupported time interval. Refer to section 3.1 of WiDE documentation for supported intervals.' },
    { key: 'required', value: 'A time unit is required.' }
];

export const AggregationMode: ContextualField = {
    modelName: 'mode',
    propertyType: PropertyType.AggregationMode,
    propertyName: 'aggregationModeProperty',
    label: 'Aggregation mode',
    placeholder: 'Aggregation mode',
    required: false,
    icon: 'fa-calculator',
    extraData: {
        subProperty: 'aggregation'
    }
};

export const AggregationPeriod: ContextualField = {
    modelName: 'period',
    propertyType: PropertyType.Frequency,
    label: 'Aggregation period',
    placeholder: 'Aggregation period',
    propertyName: 'aggregationPeriodProperty',
    errorName: 'aggregationPeriodPropertyInterval',
    required: false,
    icon: 'fa-calendar-times-o',
    extraData: {
        intervalUnitName: 'unit',
        subProperty: 'aggregation'
    },
    messages: frequencyErrors
};

export const AggregationPeriodBuilder: (c) => ContextualField = (config) => {
    const aggregationPeriod = angular.copy(AggregationPeriod);
    if (config && config.aggregation) {
        aggregationPeriod.disabled = config.aggregation.mode === AggregationModeEnum.None;
    } else {
        config.aggregation = new AggregationConfiguration();
        config.aggregation.mode = AggregationModeEnum.None;
        config.aggregation.period = new TimeInterval(1, 2);
        aggregationPeriod.disabled = true;
    }
    return aggregationPeriod;
};

export const Background: ContextualField = {
    modelName: 'backgroundUrl',
    propertyType: PropertyType.Background,
    propertyName: 'backgroundUrlProperty',
    label: 'Background',
    icon: 'fa-picture-o',
    required: false,
    extraData: {
        target: ''
    }
};

export const Color: ContextualField = {
    modelName: 'color',
    propertyType: PropertyType.Color,
    propertyName: 'colorProperty',
    label: 'Color',
    placeholder: 'Color',
    icon: 'fa-square',
    extraData: {
        subProperty: 'options'
    }
};

export const CustomProperties: ContextualField = {
    modelName: 'customProperties',
    propertyType: PropertyType.UserFields,
    label: 'User fields',
    placeholder: '0 properties',
    propertyName: 'userFieldsProperty',
    required: false,
    icon: 'fa-user-plus',
    messages: [
        { key: 'duplicate', value: 'Duplicated user field' },
        { key: 'required', value: 'User field name is required' },
        { key: 'invalidCharacter', value: 'Invalid character' }
    ],
    extraData: {}
};

export const IsTitleVisible: ContextualField = {
    modelName: 'isTitleVisible',
    propertyName: 'isTitleVisibleProperty',
    propertyType: PropertyType.Boolean,
    placeholder: 'Show title',
    label: 'Show title',
    icon: 'fa-header',
    extraData: {
        subProperty: 'options'
    }
};

export const IsLegendVisible: ContextualField = {
    modelName: 'isLegendVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isLegendVisibleProperty',
    placeholder: 'Show Legend',
    label: 'Show Legend',
    icon: 'fa-list',
    extraData: {
        subProperty: 'options'
    }
};

export const IsHorizontalLineVisible: ContextualField = {
    modelName: 'isHorizontalLineVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isHorizontalLineVisibleProperty',
    label: 'Horizontal grid lines',
    placeholder: 'Horizontal grid lines',
    //iconText: '#',
    icon: 'fa-bars',
    extraData: {
        subProperty: 'options'
    }
};

export const IsHorizontalGridLineVisible: ContextualField = {
    modelName: 'isHorizontalGridlinesVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isHorizontalGridlinesVisibleProperty',
    label: 'Horizontal grid lines',
    placeholder: 'Horizontal grid lines',
    iconText: 'H',
    extraData: {
        subProperty: 'options'
    }
};

export const IsHorizontalGridLineVisibleDisplay: ContextualField = {
    modelName: 'isHorizontalGridlinesVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isHorizontalGridlinesVisibleProperty',
    label: 'Horizontal grid lines',
    placeholder: 'Horizontal grid lines',
    iconText: 'H',
    extraData: {
        subProperty: 'options.display'
    }
};

export const IsVerticalGridlinesVisibleDisplay: ContextualField = {
    modelName: 'isVerticalGridlinesVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isVerticalGridlinesVisibleProperty',
    placeholder: 'Vertical grid lines',
    label: 'Vertical grid lines',
    iconText: 'V',
    extraData: {
        subProperty: 'options.display'
    }
};

export const IsEndDateIncluded: ContextualField = {
    modelName: 'isEndDateIncluded',
    propertyType: PropertyType.Boolean,
    label: 'Include end date',
    placeholder: 'Include end date',
    propertyName: 'isEndDateIncludedProperty',
    required: false,
    iconText: ']'
};

export const IsLabelsVisible: ContextualField = {
    modelName: 'isLabelsVisible',
    propertyType: PropertyType.Boolean,
    propertyName: 'isLabelsVisibleProperty',
    label: 'Labels',
    placeholder: 'Labels',
    icon: 'fa-hashtag',
    extraData: { subProperty: 'options' }
};

export const LongComment: ContextualField = {
    modelName: 'longComment',
    propertyName: 'longCommentProperty',
    propertyType: PropertyType.Text,
    placeholder: 'Long comment',
    label: 'Long comment',
    icon: 'fa-commenting-o',
    type: 'text',
    messages: [
    ]
};

export const periodValidationMin = {
    key: 'min',
    isValid: (frequency: IFrequency) => {
        return frequency.interval === null
            || frequency.interval === undefined
            || (frequency.interval > 0);
    }
};
export const periodValidationRequired = {
    key: 'required',
    isValid: (frequency: IFrequency) => {
        return frequency.interval === null
            || frequency.interval === undefined
            || frequency.intervalTimeUnit >= 0;
    }
};
export const periodValidation = [periodValidationMin, periodValidationRequired];

export const MaxPeriod: ContextualField = {
    modelName: 'maxPeriod',
    propertyType: PropertyType.Frequency,
    label: 'Maximum period',
    placeholder: 'Maximum period',
    propertyName: 'maxPeriodProperty',
    errorName: 'maxPeriodPropertyInterval',
    required: false,
    icon: 'fa-calendar-plus-o',
    extraData: {
        intervalUnitName: 'unit',
        frequencyValidationRules: periodValidation
    },
    messages: frequencyErrors
};

export const MinPeriod: ContextualField = {
    modelName: 'minPeriod',
    propertyType: PropertyType.Frequency,
    label: 'Minimum period',
    placeholder: 'Minimum period',
    propertyName: 'minPeriodProperty',
    errorName: 'minPeriodPropertyInterval',
    required: false,
    icon: 'fa-calendar-minus-o',
    extraData: {
        intervalUnitName: 'unit',
        frequencyValidationRules: periodValidation
    },
    messages: frequencyErrors
};

export const PeriodReference: ContextualField = {
    modelName: 'alignedOn',
    propertyType: PropertyType.Select,
    label: 'Aligned on',
    placeholder: 'Aligned on',
    propertyName: 'alignedOnProperty',
    required: false,
    icon: 'fa-calendar-check-o',
    extraData: {
        options: [
            { key: PeriodReferenceEnum.Begin, value: 'Begin' },
            { key: PeriodReferenceEnum.Middle, value: 'Middle' },
            { key: PeriodReferenceEnum.End, value: 'End' }
        ]
    }
};

export const Name: ContextualField = {
    modelName: 'name',
    propertyType: PropertyType.Text,
    label: 'Name',
    placeholder: 'Name',
    propertyName: 'nameProperty',
    required: true,
    icon: 'fa-id-badge',
    messages: [
        { key: 'required', value: 'A name is required' },
        { key: 'duplicate', value: 'This name is already in use' },
        { key: 'forbidden', value: 'This name contains invalid character(s).' }
    ]
};

export const NameConfiguration: {
    HasGroupName: ContextualField;
    HasName: ContextualField;
    HasShortComment: ContextualField;
    HasTagShortComment: ContextualField;
    HasValueType: ContextualField;
    HasUnit: ContextualField;
} = {
    HasGroupName: {
        modelName: 'hasGroupName',
        propertyType: PropertyType.Boolean,
        label: 'Group name',
        placeholder: 'Group name',
        propertyName: 'hasGroupNameProperty',
        required: false,
        icon: 'fa-sitemap',
        extraData: { subProperty: 'nameConfiguration' }
    },
    HasName: {
        modelName: 'hasName',
        propertyType: PropertyType.Boolean,
        label: 'Identifier name',
        placeholder: 'Identifier name',
        propertyName: 'hasNameProperty',
        required: false,
        icon: 'fa-id-badge',
        extraData: { subProperty: 'nameConfiguration' }
    },
    HasShortComment: {
        modelName: 'hasShortComment',
        propertyType: PropertyType.Boolean,
        label: 'Short comment',
        placeholder: 'Short comment',
        propertyName: 'hasShortCommentProperty',
        required: false,
        icon: ['fa-id-badge', 'fa-comment-o'],
        extraData: { subProperty: 'nameConfiguration' }
    },
    HasTagShortComment: {
        modelName: 'hasTagShortComment',
        propertyType: PropertyType.Boolean,
        label: 'Tag short comment',
        placeholder: 'Tag short comment',
        propertyName: 'hasTagShortCommentProperty',
        required: false,
        icon: ['fa-tag', 'fa-comment-o'],
        extraData: { subProperty: 'nameConfiguration' }
    },
    HasValueType: {
        modelName: 'hasValueType',
        propertyType: PropertyType.Boolean,
        label: 'Value type',
        placeholder: 'Value type',
        propertyName: 'hasValueTypeProperty',
        required: false,
        icon: 'fa-neuter',
        iconText: 'V',
        extraData: { subProperty: 'nameConfiguration' }
    },
    HasUnit: {
        modelName: 'hasUnit',
        propertyType: PropertyType.Boolean,
        label: 'Unit',
        placeholder: 'Unit',
        propertyName: 'hasUnitProperty',
        required: false,
        icon: 'fa-balance-scale',
        extraData: { subProperty: 'nameConfiguration' }
    }
};

export const ShortComment: ContextualField = {
    modelName: 'shortComment',
    propertyName: 'shortCommentProperty',
    propertyType: PropertyType.Text,
    placeholder: 'Short comment',
    label: 'Short comment',
    icon: 'fa-comment-o',
    type: 'text',
    maxLength: 16,
    messages: [
        { key: 'maxlength', value: 'Short comment can have maximum 16 characters' }
    ]
};

export const Title: ContextualField = {
    modelName: 'title',
    propertyName: 'titleProperty',
    propertyType: PropertyType.Text,
    placeholder: 'Title',
    label: 'Title',
    icon: 'fa-header',
    extraData: {
        subProperty: 'options'
    }
};

export const YMax: ContextualField = {
    modelName: 'yMax',
    propertyType: PropertyType.Number,
    propertyName: 'yMaxProperty',
    label: 'Y max',
    placeholder: 'Y max',
    required: false,
    icon: 'fa-long-arrow-up',
    extraData: {
        subProperty: 'options',
        decimals: 0
    }
};

export const YMin: ContextualField = {
    modelName: 'yMin',
    propertyType: PropertyType.Number,
    propertyName: 'yMinProperty',
    label: 'Y min',
    placeholder: 'Y min',
    required: false,
    icon: 'fa-long-arrow-down',
    extraData: {
        subProperty: 'options',
        decimals: 0
    }
};

export const MaxColumns: ContextualField = {
    modelName: 'maxColumns',
    propertyType: PropertyType.Number,
    label: 'Max number of records',
    placeholder: 'Max number of records',
    propertyName: 'maxColumnsProperty',
    required: false,
    icon: 'fa-columns',
    extraData: {
        min: 1,
        decimals: 0
    },
    messages: [
        {
            key: 'min',
            value: 'Max number of records must be an integer greater than 0.'
        },
        {
            key: 'required',
            value: 'Max number of records is required.'
        },
        {
            key: 'number',
            value: 'Max number of records must be an integer greater than 0.'
        },
        {
            key: 'float',
            value: 'Max number of records must be an integer greater than 0.'
        }
    ]
};

export const IsVertical: ContextualField = {
    modelName: 'isVertical',
    propertyType: PropertyType.Boolean,
    label: 'Vertical',
    placeholder: 'Vertical',
    propertyName: 'isVerticalProperty',
    required: false,
    icon: 'fa-list-alt'
};

export const RowSpacingSelect: ContextualField = {
    modelName: 'rowSpacing',
    propertyType: PropertyType.Select,
    label: 'Row spacing',
    placeholder: 'Row spacing',
    propertyName: 'rowSpacingProperty',
    icon: ['fa-arrows-v', 'fa-bars'],
    messages: [],
    extraData: {
        options: [
            { key: RowSpacing.Small, value: 'Small' },
            { key: RowSpacing.Normal, value: 'Normal' },
            { key: RowSpacing.Large, value: 'Large' }
        ]
    }
};

export const BackgroundColor: ContextualField = {
    modelName: 'backgroundColor',
    propertyType: PropertyType.Color,
    label: 'Background color',
    propertyName: 'backgroundColorProperty',
    required: false,
    icon: 'fa-square'
};

export const WidgetFields: ContextualField[] = [
    {
        modelName: 'name',
        propertyType: PropertyType.Text,
        label: 'Name',
        placeholder: 'Widget name',
        propertyName: 'nameProperty',
        required: true,
        icon: 'fa-id-badge',
        messages: [
            { key: 'required', value: 'A name is required' },
            { key: 'duplicate', value: 'This name is already in use' },
            { key: 'forbidden', value: 'This name contains invalid character(s).' }
        ]
    },
    {
        modelName: 'row',
        propertyType: PropertyType.Number,
        label: 'Row',
        propertyName: 'rowProperty',
        required: true,
        icon: 'fa-long-arrow-down',
        extraData: {
            min: 0,
            decimals: 0,
            options: {
                updateOn: 'blur'
            }
        }
    },
    {
        modelName: 'col',
        propertyType: PropertyType.Number,
        label: 'Column',
        propertyName: 'colProperty',
        required: true,
        icon: 'fa-long-arrow-right',
        extraData: {
            min: 0,
            max: 11,
            decimals: 0,
            options: {
                updateOn: 'blur'
            }
        }
    },
    {
        modelName: 'sizeX',
        propertyType: PropertyType.Number,
        label: 'Width',
        propertyName: 'sizeXProperty',
        required: true,
        icon: 'fa-arrows-h',
        extraData: {
            min: 1,
            max: 12,
            decimals: 0,
            options: {
                updateOn: 'blur'
            }
        }
    },
    {
        modelName: 'sizeY',
        propertyType: PropertyType.Number,
        label: 'Height',
        propertyName: 'sizeYProperty',
        required: true,
        icon: 'fa-arrows-v',
        extraData: {
            min: 1,
            decimals: 0,
            options: {
                updateOn: 'blur'
            }
        }
    },
    {
        modelName: 'isTitleHidden',
        propertyType: PropertyType.Boolean,
        label: 'Hide title',
        placeholder: 'Hide title',
        propertyName: 'isTitleHiddenProperty',
        required: false,
        icon: 'fa-header'
    },
    BackgroundColor
];

export const IsExportToChildApplication: ContextualField = {
    modelName: 'isExportToChildApplication',
    propertyType: PropertyType.Boolean,
    required: false,
    icon: 'fa-external-link-square fa-rotate-90',
    label: 'Export to child applications',
    propertyName: 'isExportToChildApplicationProperty'
};

export const ApprovalStrategyGuid: ContextualField = {
    modelName: 'approvalStrategyGuid',
    propertyType: PropertyType.ApprovalStrategyGuid,
    icon: 'fa-thumbs-o-up',
    label: 'Approval strategy',
    propertyName: 'approvalStrategyProperty',
};

export const ApprovalLevels: ContextualField = {
    modelName: 'approvalLevels',
    propertyType: PropertyType.ApprovalMultiLevel,
    iconText: 'L',
    label: 'Levels',
    propertyName: 'approvalLevelsProperty',
    extraData: {
        options: []
    }
};

export const Resolution: ContextualField = {
    modelName: 'resolution',
    propertyType: PropertyType.Number,
    label: 'Resolution',
    propertyName: 'resolutionProperty',
    icon: 'fa-arrows-v',
    extraData: { min: 0 },
    isValid: (value: string) => {
        return { 'dash': !!value && value !== '-' };
    },
    messages: [
        { key: 'float', value: 'Resolution must be a number' },
        { key: 'min', value: 'Resolution must be a positive number' },
        { key: 'dash', value: 'Resolution must be a positive number' }
    ]
};

export const ResolutionPercentage: ContextualField = {
    modelName: 'resolutionPercentage',
    propertyType: PropertyType.Number,
    label: 'Resolution%',
    propertyName: 'resolutionPercentageProperty',
    icon: 'fa-percent',
    extraData: { min: 0 },
    isValid: (value: string) => {
        return { 'dash': !!value && value !== '-' };
    },
    messages: [
        { key: 'float', value: 'Resolution% must be a number' },
        { key: 'min', value: 'Resolution% must be a positive number' },
        { key: 'dash', value: 'Resolution% must be a positive number' }
    ]
};

export const Min: ContextualField = {
    modelName: 'min',
    propertyType: PropertyType.Number,
    label: 'Min',
    propertyName: 'minProperty',
    icon: 'fa-long-arrow-down',
    messages: [
        { key: 'float', value: 'Min must be a number' },
        { key: 'minmax', value: 'Min must be smaller than max' }
    ]
};

export const Max: ContextualField = {
    modelName: 'max',
    propertyType: PropertyType.Number,
    label: 'Max',
    propertyName: 'maxProperty',
    icon: 'fa-long-arrow-up',
    messages: [
        { key: 'float', value: 'Max must be a number' },
        { key: 'minmax', value: 'Max must be greater than min' }
    ]
};

export const DefaultValue = {
    modelName: 'defaultValue',
    propertyType: PropertyType.Number,
    label: 'Default value',
    propertyName: 'defaultValueProperty',
    icon: 'fa-file-text-o',
    messages: [
        { key: 'float', value: 'Default value must be a number' }
    ]
};

export const TicketStatuses: ContextualField = {
    modelName: 'ticketStatuses',
    propertyType: PropertyType.TicketStatusMultiLevel,
    iconText: 'S',
    label: 'Ticket statuses',
    propertyName: 'ticketStatusesProperty',
    extraData: {
        options: []
    }
};

export const Thickness: ContextualField = {
    modelName: 'thickness',
    propertyType: PropertyType.Number,
    propertyName: 'thicknessProperty',
    label: 'Thickness',
    placeholder: 'Thickness',
    icon: ['fa-arrows-h'],
    extraData: {
        subProperty: 'options',
        min: 10,
        max: 100,
        decimals: 0
    },
    messages: [
        { key: 'min', value: 'Thickness must be a number between 10 and 100.' },
        { key: 'max', value: 'Thickness must be a number between 10 and 100.' },
        { key: 'pattern', value: 'Thickness must be a number between 10 and 100.' },
        { key: 'float', value: 'Thickness must be a number between 10 and 100.' }
    ]
};

export const ColoringMode: ContextualField = {
    modelName: 'coloringMode',
    propertyType: PropertyType.Select,
    propertyName: 'coloringModeProperty',
    label: 'Coloring mode',
    icon: 'fa-paint-brush',
    extraData: {
        subProperty: 'options',
        options: [
            { key: WidgetModels.ColoringMode.SingleColor, value: 'Single color' },
            { key: WidgetModels.ColoringMode.BySerie, value: 'Per series' }
        ]
    }
};

export const DataTypeField: ContextualField = {
    modelName: 'dataType',
    propertyName: 'dataTypeProperty',
    propertyType: PropertyType.DataType,
    placeholder: 'Data type',
    label: 'Data type',
    icon: 'fa-neuter',
    iconText: 'D',
    disabled: false,
    extraData: { disablePreviousStep: false }
};

export const ActualDataTypeField: ContextualField = {
    modelName: 'actualDataType',
    propertyName: 'actualDataTypeProperty',
    propertyType: PropertyType.Select,
    placeholder: 'Data type',
    label: 'Data type',
    icon: 'fa-neuter',
    iconText: 'D',
    disabled: false,
    extraData: {
        options: [
            { key: ActualDataType.Float, value: 'Float' },
            { key: ActualDataType.Digital, value: 'Digital' },
            { key: ActualDataType.Timestamp, value: 'Timestamp' },
            { key: ActualDataType.String, value: 'String' }
        ]
    }
};

export const AcquisitionStrategyField: ContextualField = {
    modelName: 'acquisitionStrategy',
    propertyName: 'acquisitionStrategyProperty',
    propertyType: PropertyType.Select,
    placeholder: 'Acquisition strategy',
    label: 'Strategy',
    iconText: '♘',
    disabled: false,
    extraData: {
        options: [
            { key: AcquisitionStrategy.Default, value: 'Default' },
            { key: AcquisitionStrategy.PreviousStep, value: 'Previous step' },
            { key: AcquisitionStrategy.Spot, value: 'Spot' },
        ]
    }
};

export const DigitalSetGuid: ContextualField = {
    modelName: 'digitalSetGuid',
    propertyName: 'digitalSetGuidProperty',
    propertyType: PropertyType.Select,
    placeholder: 'Digital set',
    label: 'Digital set',
    icon: 'fa-list-ol',
    required: true,
    extraData: { options: [] },
    messages: [{ key: 'required', value: 'A digital set is required' }]
};

export const IncludeChildren: ContextualField = {
    modelName: 'includeChildren',
    propertyType: PropertyType.Boolean,
    label: 'Include children',
    placeholder: 'Include children',
    propertyName: 'includeChildrenProperty',
    required: false,
    icon: 'fa-child',
    extraData: {}
};

export const GroupTypes: ContextualField = {
    modelName: 'groupTypes',
    propertyType: PropertyType.MultiGroupType,
    label: 'Group types',
    placeholder: 'Enter group types',
    propertyName: 'groupTypesProperty',
    required: false,
    icon: ['fa-sitemap', 'fa-neuter'],
    extraData: {}
};

export const GroupNames: ContextualField = {
    modelName: 'groupGuidCollection',
    propertyType: PropertyType.MultiGroup,
    label: 'Group names',
    propertyName: 'groupNamesProperty',
    required: false,
    icon: ['fa-sitemap', 'fa-id-badge'],
    extraData: {}
};

export const GroupNameSelector: ContextualField = {
    modelName: 'groupNameDisplayed',
    propertyType: PropertyType.Select,
    label: 'Group',
    propertyName: 'groupNameDisplayedProperty',
    required: false,
    icon: 'fa-sitemap',
    extraData: {
        options: [
            { key: GroupDisplayName.None, value: 'None' },
            { key: GroupDisplayName.Name, value: 'Group name' },
            { key: GroupDisplayName.UniqueName, value: 'Group unique name' },
            { key: GroupDisplayName.AbsoluteName, value: 'Group absolute name' }
        ]
    }
};

export const IsDisabledField: ContextualField = {
    modelName: 'isDisabled',
    propertyType: PropertyType.Boolean,
    label: 'Disabled',
    propertyName: 'isDisabledProperty',
    icon: 'fa-eye-slash'
};

export const ShowTarget: ContextualField = {
    modelName: 'showTarget',
    propertyType: PropertyType.Boolean,
    label: 'Show Target',
    propertyName: 'showTargetProperty',
    icon: 'fa-dot-circle-o',
    extraData: {
        subProperty: 'options'
    }
};

export const ShowAlarms: ContextualField = {
    modelName: 'showAlarms',
    propertyType: PropertyType.Boolean,
    label: 'Show alarms',
    placeholder: 'Show alarms',
    propertyName: 'showAlarmsProperty',
    required: false,
    icon: 'fa-bell'
};

export const SelectionStrategy: ContextualField = {
    modelName: 'selectionStrategy',
    propertyType: PropertyType.Select,
    label: 'Selection strategy',
    propertyName: 'selectionStrategyProperty',
    icon: 'fa-check-square-o',
    extraData: {
        options: SelectionStrategyList.enum.map(d => d)
    }
};

export const ValueType: ContextualField = {
    modelName: 'valueType',
    propertyType: PropertyType.ValueType,
    label: 'Value type',
    propertyName: 'valueTypeProperty',
    icon: 'fa-neuter',
    iconText: 'V',
    required: true,
    extraData: {},
    messages: [
        { key: 'required', value: 'A value type is required' }
    ]
};

export const ValueTypesMultiSelect: ContextualField = {
    label: 'Value Types',
    modelName: 'valueTypes',
    propertyName: 'valueTypesProperty',
    propertyType: PropertyType.MultiSelect,
    icon: 'fa-neuter',
    iconText: 'V',
    extraData: {
        options: ValueTypes.allValueTypesExceptRaw
    }
};

export const QuerySearch: ContextualField = {
    label: 'Search criteria',
    modelName: 'search',
    propertyName: 'searchProperty',
    propertyType: PropertyType.QuerySearchBar,
    icon: 'fa-search',
    extraData: {
        dummyFiltered: [],
        dummyItems: []
    },
    messages: [
        { key: 'invalid', value: 'Invalid query' }
    ]
};

export const AggregationModesMultiSelect: ContextualField = {
    label: 'Aggregation modes',
    modelName: 'aggregationModes',
    propertyName: 'aggregationModesProperty',
    propertyType: PropertyType.MultiSelect,
    icon: 'fa-calculator',
    extraData: {
        options: aggregationModes
    }
};

export const ConnectionPointGuid: ContextualField = {
    modelName: 'connectionPointGuid',
    propertyType: PropertyType.ConnectionPoint,
    propertyName: 'connectionPointProperty',
    label: 'Connection point',
    icon: 'fa-connectdevelop',
    required: true,
    messages: [
        { key: 'required', value: 'A connection point is required' }
    ],
    extraData: {
    }
};

export const GroupGuid: ContextualField = {
    propertyType: PropertyType.Group,
    modelName: 'groupGuid',
    propertyName: 'groupProperty',
    label: 'Group',
    placeholder: 'Group',
    icon: 'fa-sitemap',
    required: true,
    disabled: false,
    messages: [
        { key: 'required', value: 'A group is required' },
    ]
};

export const ApprovalLevel: ContextualField = {
    propertyType: PropertyType.Select,
    modelName: 'approvalLevel',
    propertyName: 'approvalLevelProperty',
    label: 'Approval level',
    required: true,
    icon: 'fa-thumbs-o-up',
    iconText: 'L',
    disabled: false
};

export const SortBy: ContextualField = {
    label: 'Sort by',
    modelName: 'sortBy',
    propertyName: 'sortByProperty',
    propertyType: PropertyType.Select,
    icon: 'fa-sort-amount-desc'
};

export const SortOrder: ContextualField = {
    label: 'Sort order',
    modelName: 'sortOrder',
    propertyName: 'sortOrderProperty',
    propertyType: PropertyType.Select,
    extraData: {
        options: [
            { key: DateOrder.Increasing, value: 'Increasing' },
            { key: DateOrder.Decreasing, value: 'Decreasing' }
        ]
    },
    icon: 'fa-sort-numeric-asc'
};

export const ShowLimit: ContextualField = {
    label: 'Show',
    modelName: 'limit',
    propertyName: 'limitProperty',
    propertyType: PropertyType.Select,
    extraData: {
        options: [
            { key: Limit.None, value: 'None' },
            { key: Limit.Top, value: 'Top' },
            { key: Limit.Bottom, value: 'Bottom' }
        ]
    },
    icon: 'fa-eye',
};

export const MaxNumberOfItems: ContextualField = {
    modelName: 'maxItems',
    propertyType: PropertyType.Number,
    propertyName: 'maxItemsProperty',
    label: 'Maximum number of items',
    iconText: '#',
    extraData: { min: 0 },
    required: false,
    messages: [
        { key: 'min', value: 'Maximum number of items must be an integer greater than 0.' },
        { key: 'float', value: 'Maximum number of items must be an integer greater than 0.' }
    ]
};

export const MaxNumberOfTotalItems: ContextualField = {
    modelName: 'maxTotalItems',
    propertyType: PropertyType.Number,
    propertyName: 'maxTotalItemsProperty',
    label: 'Maximum number of items',
    iconText: '#',
    extraData: { min: 0 },
    required: false,
    messages: [
        { key: 'min', value: 'Maximum number of items must be an integer greater than 0.' },
        { key: 'float', value: 'Maximum number of items must be an integer greater than 0.' }
    ]
};

export const PresentationUnit: ContextualField = {
    modelName: 'options',
    propertyType: PropertyType.Conversion,
    propertyName: 'ConversionProperty',
    label: 'Conversion type',
    placeholder: 'Conversion type',
    icon: ['fa-balance-scale', 'fa-arrows-v'],
    extraData: {
        typePropertyName: 'variableTypeGuid',
        unitPropertyName: 'unitGuid',
        allowUndefined: true
    },
    messages: [{ key: 'required', value: 'A unit is required.' }]
};

// Need to override extraData > options
export const DateRange: ContextualField = {
    modelName: 'dateRange',
    propertyType: PropertyType.DateTimePicker,
    propertyName: 'DateRangeProperty',
    label: 'Date range',
    placeholder: 'Date range',
    icon: 'fa-calendar-o',
    extraData: {
        options: {}
    }
};

export * from './AggregationColumn';
export * from './AlarmCategories';
export * from './AlarmSeverity';
export * from './AllowLogbookEntries';
export * from './Dashboard';
export * from './DateFormat';
export * from './Decimals';
export * from './Documentation';
export * from './GroupGuids';
export * from './IsBoundToGroup';
export * from './IsDependentGroupsIncluded';
export * from './IsSingleDashboardFile';
export * from './LaunchTask';
export * from './ValueFieldWidth';